import {AfterViewInit, Component, Input, OnInit} from '@angular/core';
import { faClipboardList } from '@fortawesome/free-solid-svg-icons';
import {SurveyService} from '../../../services/surveys.service';
import {TranslateService} from '@ngx-translate/core';
import {ScriptLoaderService} from 'angular-google-charts';
import {AlertService} from '../../../services/general/alert.service';
import {ToastService} from '../../../services/general/toast.service';
import {PageEvent} from '@angular/material/paginator';
import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';
import {EChartsOption} from 'echarts';
import {AiService} from '../../../services/ai.service';

@Component({
    selector: 'app-survey-all-responses',
    templateUrl: './survey-all-responses.component.html',
    styleUrls: ['./survey-all-responses.component.scss'],
    standalone: false
})
export class SurveyAllResponsesComponent implements OnInit {

  constructor(
    public surveyService: SurveyService,
    public translateService: TranslateService,
    private loaderService: ScriptLoaderService,
    private toast: ToastService,
    private aiService: AiService
  ) {

  }

  @Input() survey: any;
  @Input() surveyId: any;

  tabIndex = 0;
  loading = false;
  showPrivateInfo = false;
  questions = [];
  totalQuestions = 0;

  currentPageIndex: number = 0;
  pageSize: number = 1;

  chartOptions!: EChartsOption;
  readonly faClipboardList = faClipboardList;
  answeredCount = 0;
  draftCount = 0;
  notAnsweredCount = 0;

  ngOnInit(): void {
    this.init();
  }

  init() {
    this.loadQuestions(this.currentPageIndex + 1, this.pageSize); // Backend pages are 1-based
    this.renderChart();
  }

  loadQuestions(page: number, perPage: number) {
    this.loading = true;
    this.surveyService.surveyResponses(
      this.surveyId,
      page,
      perPage,
      (result) => {
        this.questions = result.data;
        this.totalQuestions = result.total;
        this.loading = false;
      },
      (err) => {
        this.loading = false;
      }
    );
  }

  onPageChange(event: PageEvent) {
    this.currentPageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadQuestions(this.currentPageIndex + 1, this.pageSize);
  }

  onChangeStatus(event) {
    this.loading = true;
    const model = {
      status: this.survey?.status
    };
    this.surveyService.changeSurveyStatus(this.survey?.id, model, (result) => {
      if (this.survey?.status === 'EVALUATING') {
        this.toast.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('modals.modal-survey-details.resposes-enabled')
        );
      }
      if (this.survey?.status === 'CLOSED') {
        this.toast.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('modals.modal-survey-details.resposes-blocked')
        );
      }
      this.loading = false;
    }, (err) => {
      this.survey.status = err.error?.status;
      this.loading = false;
    });
  }

  toggleAnonymousData() {
    this.showPrivateInfo = !this.showPrivateInfo;
  }

  private renderChart(): void {
    const members = this.survey?.members || [];
    const respondents = members.filter(m => m.is_respondent === true);

    this.answeredCount = respondents.filter(m => m.response_status === 'SENT').length;
    this.draftCount = respondents.filter(m => m.response_status === 'DRAFT').length;
    this.notAnsweredCount = respondents.length - this.answeredCount - this.draftCount;

    const activeSlices = [this.answeredCount, this.notAnsweredCount, this.draftCount].filter(count => count > 0).length;

    this.chartOptions = {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const label = params.name.replace(/\s*\(\d+\)\s*$/, '');
          return `${label}: ${params.value}`;
        }
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        textStyle: { color: '#666' }
      },
      series: [
        {
          name: this.translateService.instant('components.responses.responses'),
          type: 'pie',
          radius: '50%',
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: activeSlices > 1 ? 2 : 0
          },
          label: {
            show: true,
            position: 'inside',
            formatter: '{c}',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: 14
          },
          labelLine: {
            show: false
          },
          data: [
            {
              value: this.answeredCount,
              name: `${this.translateService.instant('generic.who-has-responded')} (${this.answeredCount})`,
              itemStyle: { color: '#27ae60' }
            },
            {
              value: this.notAnsweredCount,
              name: `${this.translateService.instant('generic.who-has-not-responded')} (${this.notAnsweredCount})`,
              itemStyle: { color: '#e57373' }
            },
            {
              value: this.draftCount,
              name: `${this.translateService.instant('generic.answers.draft')} (${this.draftCount})`,
              itemStyle: { color: '#ffb74d' }
            },
          ].filter(item => item.value > 0)
        }
      ]
    };
  }

  get answersSent() {
    return this.survey?.members?.filter((m) => m.is_respondent === true && m.response_status === 'SENT') || [];
  }

  get participantsCount() {
    return this.survey?.members?.filter(m => m.is_respondent === true).length || 0;
  }

  get surveyIsPrivate() {
    return this.survey?.visibility_type == 'PRIVATE';
  }

  onAiAnalysisCompleted() {
    // console.log('AI Analysis completed event received in survey-all-responses');
    // console.log('Current page:', this.currentPageIndex, 'Total questions:', this.totalQuestions, 'Page size:', this.pageSize);

    // Calcola se ci sono altre domande dopo questa
    const nextPageIndex = this.currentPageIndex + 1;
    const maxPageIndex = Math.ceil(this.totalQuestions / this.pageSize) - 1;

    if (nextPageIndex <= maxPageIndex) {
      // Ci sono altre domande, triggera l'analisi per la domanda successiva senza navigare
      // console.log('Triggering AI analysis for next question (page', nextPageIndex, ') in background');

      // Carica la domanda successiva per ottenere il suo ID
      this.surveyService.surveyResponses(
        this.surveyId,
        nextPageIndex + 1, // Backend pages are 1-based
        this.pageSize,
        (result) => {
          if (result.data && result.data.length > 0) {
            const nextQuestion = result.data[0];
            // console.log('Next question loaded:', nextQuestion.id);

            // Triggera l'analisi AI per la domanda successiva
            this.aiService.triggerSurveyAiAnalysis(
              this.surveyId,
              nextQuestion.id,
              this.survey?.attachments || [],
              nextQuestion.attachments || [],
              [], // survey_llm_analysis
              [], // question_llm_analysis
              [], // answer_llm_analysis
              (response) => {
                // console.log('AI analysis triggered for next question:', nextQuestion.id);
              },
              (error) => {
                console.error(' Error triggering AI analysis for next question:', error);
              }
            );
          }
        },
        (err) => {
          console.error('Error loading next question:', err);
        }
      );
    } else {
      // Era l'ultima domanda
      console.log('Analysis completed for all questions!');
    }
  }
}
